import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sprout } from "lucide-react";
import api from "../lib/api";

const WELCOME =
  "Welcome to Kalpavriksha Interiors & Constructions. I can help you estimate your project cost, schedule a consultation, explore our portfolio, and connect you with our team. How may I help you today?";

const SUGGESTIONS = [
  "What packages do you offer?",
  "Estimate my project cost",
  "Contact details",
  "Book a consultation",
];

function sessionId() {
  let id = localStorage.getItem("kv_chat_session");
  if (!id) {
    id = "sess-" + Math.random().toString(36).slice(2) + Date.now();
    localStorage.setItem("kv_chat_session", id);
  }
  return id;
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chips, setChips] = useState(SUGGESTIONS);
  const endRef = useRef();
  const sid = useRef(sessionId());

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { session_id: sid.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      if (Array.isArray(data.suggestions) && data.suggestions.length > 0) {
        setChips(data.suggestions);
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble right now. Please try again or use the Contact page." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((o) => !o)}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-6 right-6 z-[60] grid place-items-center w-16 h-16 rounded-full bg-primary text-primary-foreground shadow-[0_10px_40px_rgba(0,0,0,0.35)] hover:bg-accent hover:text-accent-foreground transition-colors duration-300"
        aria-label="Open chat"
      >
        {open ? <X size={26} /> : <MessageCircle size={26} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chatbot-window"
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="fixed bottom-28 right-6 z-[60] w-[min(92vw,400px)] h-[min(70vh,560px)] rounded-3xl glass shadow-[0_20px_60px_rgba(0,0,0,0.4)] flex flex-col overflow-hidden"
          >
            <div className="flex items-center gap-3 p-4 bg-primary text-primary-foreground">
              <span className="grid place-items-center w-10 h-10 rounded-full bg-accent text-accent-foreground">
                <Sprout size={20} />
              </span>
              <div className="leading-tight">
                <p className="font-serif-display text-lg">Kalpavriksha Assistant</p>
                <p className="text-xs text-primary-foreground/70">Online • replies in seconds</p>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[82%] px-4 py-2.5 text-sm leading-relaxed rounded-2xl whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-secondary text-secondary-foreground rounded-bl-sm"
                    }`}
                  >
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="px-4 py-3 rounded-2xl bg-secondary flex gap-1.5">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
              {chips.length > 0 && !loading && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {chips.map((s) => (
                    <button
                      key={s}
                      data-testid="chat-suggestion"
                      onClick={() => send(s)}
                      className="text-xs px-3 py-1.5 rounded-full border border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            <div className="p-3 border-t border-border flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Type your message…"
                className="flex-1 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted-foreground"
              />
              <button
                data-testid="chat-send"
                onClick={() => send()}
                disabled={loading}
                className="grid place-items-center w-10 h-10 rounded-full bg-primary text-primary-foreground hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50"
              >
                <Send size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
